"use client";

import { useCallback, useEffect, useId, useRef, useState, useSyncExternalStore } from "react";

import { CtaFlexible } from "@/components/shared/cta";
import { ResponsiveImage } from "@/components/shared/ResponsiveImage";
import { ModuleTimelineCarousel } from "@/lib/types";
import { cn } from "@/lib/ui/cn";

type Slide = ModuleTimelineCarousel["slides"][number];

type SlideshowProps = {
  slides: Slide[];
  autoplay: boolean;
  /** Seconds each slide is shown before advancing. */
  autoplayInterval: number;
};

const NAV_KEYS = ["ArrowLeft", "ArrowRight", "Home", "End"];
/** Horizontal pointer travel, in px, that counts as a swipe. */
const SWIPE_THRESHOLD = 50;
/** Diameter of a timeline bullet, in px. The track and fill centre on its radius. */
const DOT_SIZE = 10;
const DOT_RADIUS = DOT_SIZE / 2;

/**
 * Inter, per the design spec. next/font already self-hosts it and exposes
 * --font-inter on <html> in the (site) layout, which is where this module
 * renders. The sans-serif fallback covers contexts without that layout, such as
 * Storybook.
 */
const FONT_INTER = "font-[family-name:var(--font-inter),sans-serif]";

const CHEVRON_CLASSES =
  "absolute top-1/2 flex size-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 text-2xl leading-none text-white backdrop-blur-sm transition-colors hover:text-white/64 disabled:cursor-not-allowed disabled:opacity-30";
const CONTROL_CLASSES =
  "typography-text-small-normal text-white transition-colors hover:text-white/64";
/** Timeline chapter label: Inter 12px / 600 / 100%, per the design. */
const TIMELINE_TITLE_CLASSES = `${FONT_INTER} text-[12px] leading-none font-semibold tracking-normal`;

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

function subscribeToReducedMotion(onChange: () => void) {
  const query = window.matchMedia(REDUCED_MOTION_QUERY);
  query.addEventListener("change", onChange);
  return () => query.removeEventListener("change", onChange);
}

const getReducedMotionSnapshot = () => window.matchMedia(REDUCED_MOTION_QUERY).matches;
/** The server cannot know the preference, so assume motion is allowed. */
const getReducedMotionServerSnapshot = () => false;

/** Tracks reduced-motion, and keeps tracking it if the preference changes. */
function usePrefersReducedMotion() {
  return useSyncExternalStore(
    subscribeToReducedMotion,
    getReducedMotionSnapshot,
    getReducedMotionServerSnapshot,
  );
}

export function Slideshow({ slides, autoplay, autoplayInterval }: SlideshowProps) {
  const baseId = useId();
  const prefersReducedMotion = usePrefersReducedMotion();

  const [current, setCurrent] = useState(0);
  const [isPlaying, setIsPlaying] = useState(autoplay);
  const [isPaused, setIsPaused] = useState(false);

  const tickRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const swipeStartX = useRef<number | null>(null);

  const count = slides.length;
  const lastIndex = count - 1;
  const hasMultiple = count > 1;
  const canGoPrev = current > 0;
  const canGoNext = current < lastIndex;

  // The timeline is a story with a start and an end, so it never wraps. Autoplay
  // stops on the last slide rather than cycling back to the first.
  const isRunning = isPlaying && !isPaused && !prefersReducedMotion && canGoNext;

  const goTo = useCallback(
    (index: number) => {
      setCurrent(Math.min(Math.max(index, 0), lastIndex));
    },
    [lastIndex],
  );

  const goNext = useCallback(() => goTo(current + 1), [current, goTo]);
  const goPrev = useCallback(() => goTo(current - 1), [current, goTo]);

  const restart = useCallback(() => {
    setIsPlaying(true);
    setCurrent(0);
  }, []);

  const togglePlay = useCallback(() => {
    if (isPlaying) {
      setIsPlaying(false);
      return;
    }
    // Pressing play once the deck has run out replays it from the beginning.
    if (current === lastIndex) setCurrent(0);
    setIsPlaying(true);
  }, [isPlaying, current, lastIndex]);

  useEffect(() => {
    if (!isRunning) return;

    const timer = window.setTimeout(() => {
      const next = current + 1;
      setCurrent(next);
      // Landing on the final slide ends the run, so the control reads "Play"
      // rather than claiming to play while nothing advances.
      if (next === lastIndex) setIsPlaying(false);
    }, autoplayInterval * 1000);

    return () => window.clearTimeout(timer);
    // `current` is a dependency so the timer restarts on every slide change,
    // including changes driven by manual navigation.
  }, [isRunning, current, lastIndex, autoplayInterval]);

  /** Roving-focus arrow handling for the tab list. */
  const onNavKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!NAV_KEYS.includes(event.key)) return;
    event.preventDefault();

    let next = current;
    if (event.key === "ArrowLeft") next = Math.max(current - 1, 0);
    if (event.key === "ArrowRight") next = Math.min(current + 1, lastIndex);
    if (event.key === "Home") next = 0;
    if (event.key === "End") next = lastIndex;

    setCurrent(next);
    tickRefs.current[next]?.focus();
  };

  const onPointerDown = (event: React.PointerEvent) => {
    swipeStartX.current = event.clientX;
  };

  const onPointerUp = (event: React.PointerEvent) => {
    if (swipeStartX.current === null) return;
    const delta = event.clientX - swipeStartX.current;
    swipeStartX.current = null;

    if (Math.abs(delta) < SWIPE_THRESHOLD) return;
    // goTo clamps, so a swipe past either end is a no-op rather than a wrap.
    if (delta < 0) goNext();
    else goPrev();
  };

  /**
   * Extends arrow-key navigation to the whole module, so it also works while
   * focus is on a chevron or the CTA rather than only on the tab list.
   */
  const onContainerKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    // onNavKeyDown calls preventDefault, so this never double-advances.
    if (event.defaultPrevented) return;

    if (event.key === "ArrowLeft") {
      event.preventDefault();
      goPrev();
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      goNext();
    }
  };

  // Ticks are equal-width flex items with the dot flush left, so dot N's centre
  // sits at N * (100% / count) + DOT_RADIUS. Starting the fill at dot 0's centre
  // makes its width exactly the span to dot `current`, stopping it dead on the
  // active bullet rather than running past it.
  const fillWidth = `${(current / count) * 100}%`;
  const activeSlide = slides[current];
  const counter = `${String(current + 1).padStart(2, "0")} / ${String(count).padStart(2, "0")}`;

  return (
    <div
      className="gap-gap-element-lg flex flex-col"
      onKeyDown={onContainerKeyDown}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocusCapture={() => setIsPaused(true)}
      onBlurCapture={() => setIsPaused(false)}
    >
      <div className="grid">
        {slides.map((slide, index) => {
          const isActive = index === current;

          return (
            <div
              key={slide._key}
              id={`${baseId}-panel-${index}`}
              role="tabpanel"
              aria-labelledby={`${baseId}-tab-${index}`}
              inert={!isActive}
              className={cn(
                // The copy column has a 320px floor and both columns get
                // min-w-0, so a long title can never push into the stage.
                "gap-gap-element-xl desktop:grid-cols-[minmax(320px,1fr)_minmax(0,700px)] desktop:items-center col-start-1 row-start-1 grid grid-cols-1",
                "desktop:grid-rows-none grid-rows-[1fr_auto]",
                "motion-safe:transition-opacity motion-safe:duration-500",
                isActive ? "opacity-100" : "pointer-events-none opacity-0",
              )}
            >
              <div className="gap-gap-element-sm flex min-w-0 flex-col">
                {slide.eyebrow && (
                  <p className="typography-text-small-normal text-white/64">{slide.eyebrow}</p>
                )}
                <h3
                  className={cn(
                    FONT_INTER,
                    "tablet:text-[46px] desktop:text-[64px] desktop:leading-[0.94] text-[36px] leading-[1] font-bold tracking-normal break-words text-white",
                  )}
                >
                  {slide.title}
                </h3>
                {slide.subtitle && (
                  <p
                    className={cn(
                      FONT_INTER,
                      "desktop:text-[19px] text-[16px] leading-[1.48] font-normal text-white/64",
                    )}
                  >
                    {slide.subtitle}
                  </p>
                )}

                {slide.entries && slide.entries.length > 0 && (
                  // Below desktop the artwork is too small to read, so the
                  // milestones baked into it are rendered as real text instead.
                  <ul className="gap-gap-element-md desktop:hidden mt-4 flex flex-col">
                    {slide.entries.map((entry) => (
                      <li key={entry._key} className="gap-gap-element-sm flex flex-col">
                        <div className="gap-gap-element-2xs flex flex-wrap items-baseline">
                          {entry.year && (
                            <span className="typography-text-small-normal text-white/64">
                              {entry.year}
                            </span>
                          )}
                          {entry.tag && (
                            <span className="typography-text-small-bold text-emerald-light uppercase">
                              {entry.tag}
                            </span>
                          )}
                        </div>
                        <p className="typography-text-medium-bold text-white">{entry.headline}</p>
                        {entry.description && (
                          <p className="typography-text-small-normal text-white/64">
                            {entry.description}
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="flex min-w-0 flex-col">
                {/*
                mx-auto centres the stage once the layout stacks. The chevrons sit
                inside the panel: hung outside, a 700px stage flush to the
                container edge would push the right one past the container and the
                left one into the copy column at narrow desktop widths.
              */}
                <div className="relative mx-auto w-full max-w-[700px]">
                  <div
                    onPointerDown={onPointerDown}
                    onPointerUp={onPointerUp}
                    className="relative aspect-square w-full touch-pan-y overflow-hidden rounded-3xl border border-white/16 bg-white/4"
                  >
                    {slide.artwork?.image && (
                      <ResponsiveImage
                        src={slide.artwork}
                        fill
                        className="object-contain"
                        sizes="(max-width: 1023px) 100vw, 700px"
                      />
                    )}
                  </div>

                  {hasMultiple && (
                    <>
                      <button
                        type="button"
                        onClick={goPrev}
                        disabled={!canGoPrev}
                        aria-label="Previous slide"
                        className={cn(CHEVRON_CLASSES, "left-3")}
                      >
                        <span aria-hidden="true">&#10094;</span>
                      </button>
                      <button
                        type="button"
                        onClick={goNext}
                        disabled={!canGoNext}
                        aria-label="Next slide"
                        className={cn(CHEVRON_CLASSES, "right-3")}
                      >
                        <span aria-hidden="true">&#10095;</span>
                      </button>
                    </>
                  )}

                  {/*
                  The 21px radius and Inter 14px/600/100% label come from the
                  design. The type classes carry `!` because Button's size
                  variants apply `typography-text-*` custom @utility classes,
                  which set family, size, weight and leading together and which
                  tailwind-merge cannot recognise as conflicting. Same technique
                  button.tsx uses for variant padding.
                */}
                  {slide.cta?.label && (
                    <div className="desktop:right-6 desktop:bottom-6 absolute right-4 bottom-4">
                      <CtaFlexible
                        cta={slide.cta as Parameters<typeof CtaFlexible>[0]["cta"]}
                        className={`${FONT_INTER}! rounded-[21px] text-[14px]! leading-none! font-semibold! tracking-normal`}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="gap-gap-element-md flex flex-col">
        <div className="relative">
          <div
            aria-hidden="true"
            className="absolute right-0 left-0 h-px -translate-y-1/2 bg-white/16"
            style={{ top: DOT_RADIUS }}
          />
          <div
            aria-hidden="true"
            className="absolute h-px -translate-y-1/2 bg-white motion-safe:transition-[width] motion-safe:duration-500"
            style={{ top: DOT_RADIUS, left: DOT_RADIUS, width: fillWidth }}
          />

          {/* Tablet and up: the full chapter timeline doubles as the tab list. */}
          <div
            role="tablist"
            aria-label="Timeline chapters"
            onKeyDown={onNavKeyDown}
            className="tablet:flex relative hidden"
          >
            {slides.map((slide, index) => {
              const isActive = index === current;

              return (
                <button
                  key={slide._key}
                  ref={(node) => {
                    tickRefs.current[index] = node;
                  }}
                  type="button"
                  id={`${baseId}-tab-${index}`}
                  role="tab"
                  aria-selected={isActive}
                  aria-controls={`${baseId}-panel-${index}`}
                  tabIndex={isActive ? 0 : -1}
                  onClick={() => goTo(index)}
                  // No flex gap, and min-w-0, so a long label cannot push its tick
                  // wider than the others. Equal widths are what keep the fill
                  // arithmetic landing on each dot.
                  // gap-3 (12px) separates dot from chapter and chapter from
                  // year, per the design.
                  className="group flex min-w-0 flex-1 flex-col items-start gap-3 pr-4 text-left"
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      "shrink-0 rounded-full transition-colors",
                      isActive ? "bg-white" : "bg-white/32 group-hover:bg-white/64",
                    )}
                    style={{ width: DOT_SIZE, height: DOT_SIZE }}
                  />
                  {/* Always white, per the design. The active chapter is
                      signalled by the dot and the progress fill, not by the
                      label colour. */}
                  <span className={cn(TIMELINE_TITLE_CLASSES, "break-words text-white")}>
                    {slide.chapter}
                  </span>
                  {slide.year && (
                    <span className="typography-text-tiny-normal text-white/40">{slide.year}</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Mobile: the timeline collapses to the active chapter only. */}
          <div className="tablet:hidden flex flex-col gap-3 pt-3">
            <span className={cn(TIMELINE_TITLE_CLASSES, "text-white")}>{activeSlide.chapter}</span>
            {activeSlide.year && (
              <span className="typography-text-tiny-normal text-white/40">{activeSlide.year}</span>
            )}
          </div>
        </div>

        <div className="gap-gap-element-md flex items-center">
          {hasMultiple && (
            <>
              <button
                type="button"
                onClick={togglePlay}
                aria-label={isPlaying ? "Pause slideshow" : "Play slideshow"}
                className={CONTROL_CLASSES}
              >
                {isPlaying ? "Pause" : "Play"}
              </button>
              <button type="button" onClick={restart} className={CONTROL_CLASSES}>
                Restart
              </button>
            </>
          )}
          <span className="typography-text-small-normal text-white/64 tabular-nums">{counter}</span>
        </div>
      </div>
    </div>
  );
}
